import { create } from 'zustand'
import { HistoryItem } from '../types'

interface HistoryState {
  // History items
  items: HistoryItem[]
  setItems: (items: HistoryItem[]) => void
  addItem: (item: HistoryItem) => void
  removeItem: (id: string) => void
  updateItem: (id: string, updates: Partial<HistoryItem>) => void
  toggleFavorite: (id: string) => void
  
  // Filtered/Sorted items
  filteredItems: HistoryItem[]
  setFilteredItems: (items: HistoryItem[]) => void
  
  // Filters
  searchQuery: string
  setSearchQuery: (query: string) => void
  styleFilter: string | null
  setStyleFilter: (style: string | null) => void
  favoritesOnly: boolean
  setFavoritesOnly: (value: boolean) => void
  
  // Selection
  selectedItems: string[]
  toggleSelection: (id: string) => void
  selectAll: () => void
  clearSelection: () => void
  
  // Statistics
  stats: { 
    totalTransformations: number
    totalSaved: number
    favoriteCount: number
    transformationsByStyle: Record<string, number>
    weeklyActivity: { date: string; count: number }[]
    monthlyStats: { month: string; transformations: number; saved: number }[]
  } | null
  setStats: (stats: NonNullable<HistoryState['stats']> | null) => void
  
  // Loading state
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  
  // Pagination
  currentPage: number
  setCurrentPage: (page: number) => void
  itemsPerPage: number
  setItemsPerPage: (count: number) => void
  
  // Filter actions
  applyFilters: () => void
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  items: [],
  filteredItems: [],
  searchQuery: '',
  styleFilter: null,
  favoritesOnly: false,
  selectedItems: [],
  stats: null,
  isLoading: false,
  currentPage: 1,
  itemsPerPage: 10,
  
  setItems: (items) => set({ items, filteredItems: items }),
  
  addItem: (item) => set((state) => ({
    items: [item, ...state.items],
    filteredItems: [item, ...state.filteredItems],
  })),
  
  removeItem: (id) => set((state) => ({
    items: state.items.filter((item) => item.id !== id),
    filteredItems: state.filteredItems.filter((item) => item.id !== id),
    selectedItems: state.selectedItems.filter((itemId) => itemId !== id),
  })),
  
  updateItem: (id, updates) => set((state) => ({
    items: state.items.map((item) => 
      item.id === id ? { ...item, ...updates } : item
    ),
    filteredItems: state.filteredItems.map((item) => 
      item.id === id ? { ...item, ...updates } : item
    ),
  })),
  
  toggleFavorite: (id) => {
    const item = get().items.find((i) => i.id === id)
    if (item) {
      get().updateItem(id, { favorite: !item.favorite })
    }
  },
  
  setFilteredItems: (items) => set({ filteredItems: items }),
  
  setSearchQuery: (query) => {
    set({ searchQuery: query })
    get().applyFilters()
  },
  
  setStyleFilter: (style) => {
    set({ styleFilter: style })
    get().applyFilters()
  },
  
  setFavoritesOnly: (value) => {
    set({ favoritesOnly: value })
    get().applyFilters()
  },
  
  toggleSelection: (id) => set((state) => ({
    selectedItems: state.selectedItems.includes(id)
      ? state.selectedItems.filter((itemId) => itemId !== id)
      : [...state.selectedItems, id]
  })),
  
  selectAll: () => set((state) => ({
    selectedItems: state.filteredItems.map((item) => item.id)
  })),
  
  clearSelection: () => set({ selectedItems: [] }),
  
  setStats: (stats) => set({ stats }),
  
  setIsLoading: (loading) => set({ isLoading: loading }),
  
  setCurrentPage: (page) => set({ currentPage: page }),
  
  setItemsPerPage: (count) => set({ itemsPerPage: count, currentPage: 1 }),
  
  applyFilters: () => {
    const { items, searchQuery, styleFilter, favoritesOnly } = get()
    let filtered = [...items]
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (item) =>
          item.original.toLowerCase().includes(query) ||
          item.humanized.toLowerCase().includes(query) ||
          item.style.toLowerCase().includes(query)
      )
    }
    
    if (styleFilter) {
      filtered = filtered.filter((item) => item.style === styleFilter)
    }
    
    if (favoritesOnly) {
      filtered = filtered.filter((item) => item.favorite)
    }
    
    set({ filteredItems: filtered, currentPage: 1 })
  },
}))
